import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import GlassCard from "../components/GlassCard";
import PrimaryButton, { GlassButton } from "../components/PrimaryButton";
import BottomNav from "../components/BottomNav";
import PageShell from "../components/PageShell";
import { Plus, Lock, Trash2, ArrowLeft } from "lucide-react";

const JournalList = () => {
  const nav = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/journal").then((r) => setEntries(r.data || [])).finally(() => setLoading(false));
  }, []);

  const firstLine = (e) =>
    (e.title && e.title.trim()) ||
    (e.content || "").split(/\r?\n/).find((l) => l.trim()) ||
    "Untitled";

  return (
    <PageShell showSettings>
      <div className="pt-16 pb-32 max-w-2xl mx-auto flex flex-col gap-6 well-page-enter">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-[#2A3B32]">Journal</h1>
          <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#8A9C91]">
            <Lock size={12} /> Private to you — never shared
          </p>
        </div>

        <PrimaryButton onClick={() => nav("/journal/new")} data-testid="journal-new-btn">
          <Plus size={20} className="mr-2" /> New Entry
        </PrimaryButton>

        <div className="flex flex-col gap-3">
          {!loading && entries.length === 0 && (
            <GlassCard className="text-center" data-testid="journal-empty">
              <p className="text-[#4A5D52]">Your journal is empty.</p>
              <p className="mt-1 text-xs text-[#8A9C91]">Tap New Entry to start writing.</p>
            </GlassCard>
          )}
          {entries.map((e) => (
            <button
              key={e.entry_id}
              onClick={() => nav(`/journal/${e.entry_id}`)}
              className="text-left"
              data-testid={`journal-item-${e.entry_id}`}
            >
              <GlassCard className="hover:bg-white/55 transition-colors">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-[#2A3B32] truncate">{firstLine(e)}</p>
                  <p className="text-xs text-[#8A9C91] whitespace-nowrap">
                    {new Date(e.created_at).toLocaleDateString()}
                  </p>
                </div>
                {e.title && (
                  <p className="mt-2 text-sm text-[#4A5D52] line-clamp-1">{(e.content || "").slice(0, 120)}…</p>
                )}
              </GlassCard>
            </button>
          ))}
        </div>
      </div>
      <BottomNav />
    </PageShell>
  );
};

export const JournalNew = () => {
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await api.post("/journal", { title: title.trim(), content: content.trim() });
      nav("/journal", { replace: true });
    } finally { setSaving(false); }
  };

  return (
    <PageShell>
      <div className="pt-16 pb-32 max-w-2xl mx-auto flex flex-col gap-6 well-page-enter">
        <div className="flex items-center justify-between">
          <button onClick={() => nav(-1)} className="text-[#4A5D52] flex items-center gap-1 text-sm" data-testid="journal-new-back">
            <ArrowLeft size={18} /> Back
          </button>
          <p className="text-xs text-[#8A9C91] inline-flex items-center gap-1.5">
            <Lock size={12} /> Private
          </p>
        </div>

        <GlassCard className="!p-3 sm:!p-5">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (optional)"
            data-testid="journal-title-input"
            className="w-full bg-transparent text-[#2A3B32] text-2xl font-semibold focus:outline-none placeholder:text-[#8A9C91] px-2 py-2"
            autoFocus
          />
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write whatever's on your mind…"
            rows={18}
            data-testid="journal-content-input"
            className="mt-2 w-full bg-transparent text-[#2A3B32] focus:outline-none resize-none text-base leading-relaxed placeholder:text-[#8A9C91] px-2"
          />
        </GlassCard>

        <div className="max-w-md mx-auto w-full flex flex-col gap-3">
          <PrimaryButton onClick={save} disabled={saving || !content.trim()} data-testid="journal-save-btn">
            {saving ? "Saving…" : "Save Entry"}
          </PrimaryButton>
          <GlassButton onClick={() => nav("/journal")} data-testid="journal-cancel-btn">Cancel</GlassButton>
        </div>
      </div>
    </PageShell>
  );
};

export const JournalDetail = () => {
  const { entryId } = useParams();
  const nav = useNavigate();
  const [entry, setEntry] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get(`/journal/${entryId}`).then((r) => setEntry(r.data)).catch(() => nav("/journal", { replace: true }));
  }, [entryId, nav]);

  const remove = async () => {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await api.delete(`/journal/${entryId}`);
      nav("/journal", { replace: true });
    } finally { setDeleting(false); }
  };

  if (!entry) return <div className="p-12 text-center text-[#4A5D52]">Loading…</div>;

  return (
    <PageShell showSettings>
      <div className="pt-16 pb-32 max-w-2xl mx-auto flex flex-col gap-6 well-page-enter">
        <div className="flex items-center justify-between">
          <button onClick={() => nav("/journal")} className="text-[#4A5D52] flex items-center gap-1 text-sm" data-testid="journal-detail-back">
            <ArrowLeft size={18} /> Journal
          </button>
          <button onClick={remove} disabled={deleting} className="text-[#B85C5C] flex items-center gap-1 text-sm" data-testid="journal-delete-btn">
            <Trash2 size={16} /> {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>

        <GlassCard>
          {entry.title && <h1 className="text-2xl font-bold text-[#2A3B32]">{entry.title}</h1>}
          <p className="mt-2 text-xs text-[#8A9C91]">{new Date(entry.created_at).toLocaleString()}</p>
          <p className="mt-6 text-base text-[#2A3B32] leading-relaxed whitespace-pre-wrap">{entry.content}</p>
        </GlassCard>
      </div>
      <BottomNav />
    </PageShell>
  );
};

export default JournalList;
